import { useAuth0 } from '@auth0/auth0-react';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import type { ParsedFile } from '@/features/parties/import/lib/types';
import { UploadStep } from '@/features/parties/import/steps/UploadStep';
import { useEquipmentCatalogue, useOrganisationEquipmentTypes } from '@my/directory-api/hooks';
import { ErrorBanner } from '@my/ui';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { autoMatchResourceColumns } from './lib/columnMatch';
import { buildResourceDrafts } from './lib/buildDrafts';
import {
  buildEquipmentIndex,
  buildEquipmentLabelIndex,
  buildEquipmentOptions,
} from './lib/equipment';
import {
  DEFAULT_RESOURCE_IMPORT_DEFAULTS,
  type ImportResourceType,
  type ResourceColumnMapping,
  type ResourceImportDefaults,
  type ResourceImportDraft,
  type WizardStep,
} from './lib/types';
import { CommitStep } from './steps/CommitStep';
import { MappingStep } from './steps/MappingStep';
import { PreviewStep } from './steps/PreviewStep';
import { TypeStep } from './steps/TypeStep';

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 'type', label: 'Resource type' },
  { id: 'upload', label: 'Upload' },
  { id: 'map', label: 'Map columns' },
  { id: 'preview', label: 'Preview & fix' },
  { id: 'commit', label: 'Import' },
];

/**
 * Resources tab of Settings → Import. A 5-step wizard that turns a JSON / CSV /
 * Excel fleet or driver file into resources:
 *   pick type → upload → map columns → preview & fix → commit per-row.
 *
 * The resource type comes first because it decides the rest: which fields exist
 * (mobile / email / equipment type), what `reference` means, and how the
 * equipment-type column resolves. Columns the Resource record can't store
 * (make, model, chassis number…) are simply left unmapped.
 *
 * Renders as a tab section — the page header + tab strip live in the parent
 * `ImportPage`. Owner / Admin only (gated at the route).
 */
export function ResourcesImportTab() {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const [step, setStep] = useState<WizardStep>('type');
  const [type, setType] = useState<ImportResourceType | null>(null);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [mapping, setMapping] = useState<ResourceColumnMapping>({});
  const [defaults, setDefaults] = useState<ResourceImportDefaults>(
    DEFAULT_RESOURCE_IMPORT_DEFAULTS,
  );
  const [drafts, setDrafts] = useState<ResourceImportDraft[]>([]);
  const [excludedRefs, setExcludedRefs] = useState<Set<string>>(() => new Set());

  // Equipment types for the chosen resource type (Trailer / Rigid only).
  const equipmentTypesQuery = useOrganisationEquipmentTypes(orgId);
  const catalogueQuery = useEquipmentCatalogue();
  const equipmentLabelByKey = useMemo(
    () => buildEquipmentLabelIndex(catalogueQuery.data),
    [catalogueQuery.data],
  );
  const equipmentOptions = useMemo(
    () =>
      type
        ? buildEquipmentOptions(equipmentTypesQuery.data ?? [], equipmentLabelByKey, type)
        : [],
    [equipmentTypesQuery.data, equipmentLabelByKey, type],
  );
  const equipmentIndex = useMemo(() => buildEquipmentIndex(equipmentOptions), [equipmentOptions]);

  const stepIndex = STEPS.findIndex((s) => s.id === step);

  const patchDraft = (rowRef: string, patch: Partial<ResourceImportDraft>) =>
    setDrafts((prev) => prev.map((d) => (d.rowRef === rowRef ? { ...d, ...patch } : d)));

  const toggleExclude = (rowRef: string) =>
    setExcludedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(rowRef)) next.delete(rowRef);
      else next.add(rowRef);
      return next;
    });

  const setAllExcluded = (excluded: boolean) =>
    setExcludedRefs(excluded ? new Set(drafts.map((d) => d.rowRef)) : new Set());

  // Changing the type invalidates the mapping (different field set), so re-seed
  // it from the headers we already have.
  const handleTypeChange = (next: ImportResourceType) => {
    setType(next);
    setMapping(parsed ? autoMatchResourceColumns(parsed.headers, next) : {});
  };

  const handleParsed = (next: ParsedFile, sourceFile: File) => {
    setParsed(next);
    setFile(sourceFile);
    if (type) setMapping(autoMatchResourceColumns(next.headers, type));
  };

  // Materialise drafts when leaving the mapping step, then advance to preview.
  const handleMappingContinue = () => {
    if (!parsed || !type) return;
    setDrafts(
      buildResourceDrafts({ parsed, mapping, type, defaults, equipmentIndex, equipmentOptions }),
    );
    setExcludedRefs(new Set());
    setStep('preview');
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto pb-4">
      {/* Stepper */}
      <ol className="flex flex-wrap items-center gap-2 text-xs">
        {STEPS.map((s, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          return (
            <li key={s.id} className="flex items-center gap-2">
              <span
                className={
                  'flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ' +
                  (active
                    ? 'bg-purple-600 text-white'
                    : done
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-500')
                }
              >
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={active ? 'font-medium text-slate-900' : 'text-slate-500'}>
                {s.label}
              </span>
              {i < STEPS.length - 1 && <span className="mx-1 text-slate-300">→</span>}
            </li>
          );
        })}
      </ol>

      <section className="rounded-md border border-slate-200 bg-white p-4">
        {step === 'type' && (
          <TypeStep
            type={type}
            onTypeChange={handleTypeChange}
            onContinue={() => setStep('upload')}
          />
        )}

        {step === 'upload' && (
          <UploadStep
            parsed={parsed}
            file={file}
            onParsed={handleParsed}
            onBack={() => setStep('type')}
            onContinue={() => setStep('map')}
          />
        )}

        {step === 'map' && parsed && type && (
          <MappingStep
            parsed={parsed}
            type={type}
            mapping={mapping}
            defaults={defaults}
            equipmentOptions={equipmentOptions}
            onMappingChange={setMapping}
            onDefaultsChange={setDefaults}
            onBack={() => setStep('upload')}
            onContinue={handleMappingContinue}
          />
        )}

        {step === 'preview' && type && (
          <PreviewStep
            drafts={drafts}
            type={type}
            equipmentOptions={equipmentOptions}
            excludedRefs={excludedRefs}
            onDraftChange={patchDraft}
            onToggleExclude={toggleExclude}
            onSetAllExcluded={setAllExcluded}
            onBack={() => setStep('map')}
            onContinue={() => setStep('commit')}
          />
        )}

        {step === 'commit' &&
          type &&
          (orgId ? (
            <CommitStep
              drafts={drafts}
              type={type}
              excludedRefs={excludedRefs}
              organisationId={orgId}
              onDraftChange={patchDraft}
              onBack={() => setStep('preview')}
              onDone={() => navigate('/settings/resources')}
            />
          ) : (
            <ErrorBanner
              title="No organisation"
              message="Couldn't resolve the current organisation — sign in again or pick an organisation, then retry the import."
            />
          ))}
      </section>
    </main>
  );
}
